import type { WhatsAppInboxMessage } from '@zrsystem/contracts';
import { useState, type ReactElement } from 'react';

import { FormattedChatText } from '@/features/conversation/components/formatted-chat-text.js';
import { useWhatsAppInboxMediaObjectUrl } from '@/features/notification/hooks/use-whatsapp-inbox-media-object-url.js';
import { formatWhatsAppInboxTimestamp } from '@/features/notification/lib/whatsapp-inbox-timestamp.js';

export interface WhatsAppInboxImageBubbleProps {
  readonly message: WhatsAppInboxMessage;
}

function resolveImageUrl(message: WhatsAppInboxMessage): string | null {
  const mediaUrl = message.media?.url ?? message.mediaUrl;
  if (mediaUrl === null || mediaUrl.length === 0) {
    return null;
  }
  return mediaUrl;
}

export function WhatsAppInboxImageBubble({ message }: WhatsAppInboxImageBubbleProps): ReactElement {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const sourceUrl = resolveImageUrl(message);
  const media = useWhatsAppInboxMediaObjectUrl(sourceUrl, message.media?.mimetype ?? null);
  const imageUrl = media.src;
  const caption = message.body?.trim();

  return (
    <div className="whatsapp-inbox-bubble__media">
      {media.pending ? <p>Carregando imagem…</p> : null}
      {sourceUrl !== null && !media.pending && imageUrl === null ? (
        <p>Imagem indisponível</p>
      ) : null}
      {imageUrl !== null ? (
        <button
          type="button"
          className="whatsapp-inbox-bubble__image-button"
          onClick={() => {
            setLightboxOpen(true);
          }}
          aria-label="Abrir imagem"
        >
          <img
            className="whatsapp-inbox-bubble__image"
            src={imageUrl}
            alt={caption ?? 'Imagem enviada no WhatsApp'}
          />
        </button>
      ) : null}
      {caption !== undefined && caption.length > 0 ? (
        <p>
          <FormattedChatText text={caption} />
        </p>
      ) : null}
      <time className="whatsapp-inbox-bubble__time" dateTime={String(message.timestamp)}>
        {formatWhatsAppInboxTimestamp(message.timestamp)}
      </time>

      {lightboxOpen && imageUrl !== null ? (
        <div
          className="whatsapp-inbox-lightbox"
          role="dialog"
          aria-label="Visualizar imagem"
          onClick={() => {
            setLightboxOpen(false);
          }}
        >
          <img
            className="whatsapp-inbox-lightbox__image"
            src={imageUrl}
            alt={caption ?? 'Imagem enviada no WhatsApp'}
            onClick={(event) => {
              event.stopPropagation();
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
